import React from "react";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { hostUI } from "../../../../configuraciones";
import { TIPOS_DOCUMENTO_RANGO } from "./ModalDocumentosRangoHolograma";

/**
 * Bloque reutilizable dentro de cada tarjeta "Rango #i" (tanto en Envasado No Terminado como en Envasado Terminado).
 * - Si el rango ya está guardado en BD (`id_producto_holograma` > 0): muestra los documentos actuales y el botón
 *   para abrir `ModalDocumentosRangoHolograma` y subir/ver/eliminar documentos.
 * - Si el rango es nuevo (en captura antes de guardar): permite adjuntar opcionalmente archivos en cola
 *   (`rango.documentosPendientes`) para Certificado de Producto Envasado, Informe de Servicio, Análisis FQ u Otro,
 *   que se suben automáticamente en cuanto se guarda el lote/rango.
 */
const DocumentosDelRango = ({
  rango,
  onChange,
  onOpenModal = null,
  origen = "envasado_terminado"
}) => {
  const idRango = Number(rango?.id_producto_holograma);
  const esGuardado = Number.isInteger(idRango) && idRango > 0;
  const docsGuardados = Array.isArray(rango?.documentos) ? rango.documentos : [];
  const pendientes = Array.isArray(rango?.documentosPendientes) ? rango.documentosPendientes : [];

  const setPendientes = (next) => {
    if (onChange) {
      onChange({ ...rango, documentosPendientes: next });
    }
  };

  const agregarFilaPendiente = () => {
    setPendientes([
      ...pendientes,
      {
        tipo_documento: "certificado_producto_envasado",
        nombre_documento: "",
        observaciones: "",
        file: null,
        origen_documento: origen
      }
    ]);
  };

  const actualizarPendiente = (idx, patch) => {
    setPendientes(pendientes.map((item, i) => (i === idx ? { ...item, ...patch } : item)));
  };

  const quitarPendiente = (idx) => {
    setPendientes(pendientes.filter((_, i) => i !== idx));
  };

  const labelStyle = {
    display: "block",
    fontSize: "11px",
    fontWeight: 700,
    color: "#334155",
    marginBottom: "3px"
  };

  const inputStyle = {
    width: "100%",
    padding: "6px 9px",
    borderRadius: "6px",
    border: "1px solid #D8B4FE",
    fontSize: "12px",
    boxSizing: "border-box",
    background: "#fff"
  };

  return (
    <div
      style={{
        marginTop: "10px",
        padding: "10px 12px",
        background: "#F0FDF4",
        border: "1px dashed #86EFAC",
        borderRadius: "8px"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap"
        }}
      >
        <div style={{ fontSize: "12px", fontWeight: 700, color: "#166534", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <span
            style={{
              background: "#16A34A",
              color: "#fff",
              borderRadius: "4px",
              padding: "1px 6px",
              fontSize: "10px",
              fontWeight: 800,
              letterSpacing: "0.02em"
            }}
          >
            RANGO
          </span>
          <FolderSharedIcon style={{ fontSize: 16, color: "#16A34A" }} />
          <span>Documentos de este rango (opcional): Certificado de Producto Envasado / Informe / Otros</span>
          {(docsGuardados.length > 0 || pendientes.length > 0) && (
            <span
              style={{
                background: "#DCFCE7",
                color: "#15803D",
                border: "1px solid #86EFAC",
                borderRadius: "999px",
                padding: "1px 8px",
                fontSize: "11px",
                fontWeight: 700
              }}
            >
              {docsGuardados.length + pendientes.length} archivo(s)
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          {esGuardado && onOpenModal && (
            <button
              type="button"
              onClick={() => onOpenModal(rango)}
              style={{
                background: "#16A34A",
                border: "none",
                color: "#fff",
                borderRadius: "6px",
                padding: "5px 11px",
                fontSize: "11.5px",
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <UploadFileIcon style={{ fontSize: 14 }} />
              <span>Expediente del Rango ({docsGuardados.length})</span>
            </button>
          )}

          {(!esGuardado || onChange) && (
            <button
              type="button"
              onClick={() => {
                if (esGuardado && onOpenModal) {
                  onOpenModal(rango);
                } else {
                  agregarFilaPendiente();
                }
              }}
              style={{
                background: "#fff",
                border: "1px solid #22C55E",
                color: "#15803D",
                borderRadius: "6px",
                padding: "4px 10px",
                fontSize: "11px",
                fontWeight: 700,
                cursor: "pointer"
              }}
            >
              + Adjuntar documento
            </button>
          )}
        </div>
      </div>

      {/* Documentos ya guardados en servidor para este rango */}
      {docsGuardados.length > 0 && (
        <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {docsGuardados.map((d) => (
            <a
              key={d.id_documento}
              href={`${hostUI}bitacora-envasado/producto/rango/documentos/${d.id_documento}/ver`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                padding: "4px 10px",
                borderRadius: "999px",
                background: "#fff",
                border: "1px solid #86EFAC",
                color: "#166534",
                fontSize: "11px",
                fontWeight: 700,
                textDecoration: "none"
              }}
              title={d.archivo_nombre_original || d.nombre_original || d.nombre_documento}
            >
              <VisibilityIcon style={{ fontSize: 13 }} />
              <span>{d.nombre_documento}</span>
            </a>
          ))}
        </div>
      )}

      {/* Archivos en cola (cuando el rango aún no se ha guardado o se agregan en línea) */}
      {pendientes.map((item, idx) => (
        <div
          key={idx}
          style={{
            marginTop: "8px",
            padding: "9px 10px",
            background: "#fff",
            border: "1px solid #BBF7D0",
            borderRadius: "7px"
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: item.tipo_documento === "otro" ? "1.3fr 1.2fr 1.5fr auto" : "1.4fr 1.8fr auto",
              gap: "8px",
              alignItems: "end"
            }}
          >
            <div>
              <label style={labelStyle}>Tipo de documento</label>
              <select
                value={item.tipo_documento || "certificado_producto_envasado"}
                onChange={(e) => actualizarPendiente(idx, { tipo_documento: e.target.value })}
                style={inputStyle}
              >
                {TIPOS_DOCUMENTO_RANGO.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre}
                  </option>
                ))}
              </select>
            </div>

            {item.tipo_documento === "otro" && (
              <div>
                <label style={labelStyle}>Nombre del documento *</label>
                <input
                  type="text"
                  value={item.nombre_documento || ""}
                  placeholder="Ej. Dictamen de Etiqueta"
                  onChange={(e) => actualizarPendiente(idx, { nombre_documento: e.target.value })}
                  style={inputStyle}
                />
              </div>
            )}

            <div>
              <label style={labelStyle}>Archivo (PDF, JPG, PNG)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  actualizarPendiente(idx, { file: f });
                }}
                style={{ ...inputStyle, padding: "4px 6px" }}
              />
            </div>

            <div>
              <button
                type="button"
                onClick={() => quitarPendiente(idx)}
                style={{
                  background: "#FEF2F2",
                  border: "1px solid #FECACA",
                  color: "#DC2626",
                  borderRadius: "6px",
                  padding: "6px 8px",
                  fontSize: "11px",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center"
                }}
                title="Quitar documento pendiente"
              >
                <DeleteOutlineIcon style={{ fontSize: 15 }} />
              </button>
            </div>
          </div>

          <div style={{ marginTop: "6px" }}>
            <input
              type="text"
              value={item.observaciones || ""}
              placeholder="Observaciones del documento (opcional)"
              onChange={(e) => actualizarPendiente(idx, { observaciones: e.target.value })}
              style={inputStyle}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentosDelRango;

